// MediaPipe Selfie Segmentation Wrapper for client-side background removal

let segmenterPromise: Promise<any> | null = null;
let selfieSegmentationInstance: any = null;
let currentResolve: ((mask: HTMLCanvasElement) => void) | null = null;

/**
 * Initializes the SelfieSegmentation engine.
 * Downloads the WASM model from CDN (approx 3MB) if not already cached.
 */
export async function initSegmenter(onProgress?: () => void): Promise<any> {
  if (selfieSegmentationInstance) return selfieSegmentationInstance;
  if (segmenterPromise) return segmenterPromise;

  segmenterPromise = new Promise((resolve, reject) => {
    try {
      if (onProgress) onProgress();
      
      // SelfieSegmentation is loaded from CDN and exposed globally on window
      // @ts-ignore
      if (typeof SelfieSegmentation === 'undefined') {
        throw new Error('SelfieSegmentation library not loaded from CDN.');
      }

      // @ts-ignore
      const segmenter = new SelfieSegmentation({
        locateFile: (file: string) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
        }
      });

      segmenter.setOptions({
        modelSelection: 1, // 0 for general portrait (slow), 1 for landscape (faster/real-time)
      });

      segmenter.onResults((results: any) => {
        if (currentResolve) {
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = results.image.width;
          maskCanvas.height = results.image.height;
          
          const ctx = maskCanvas.getContext('2d');
          if (ctx && results.segmentationMask) {
            // Draw the segmentation mask onto our canvas
            ctx.drawImage(results.segmentationMask, 0, 0, maskCanvas.width, maskCanvas.height);
          }
          
          const resolveFn = currentResolve;
          currentResolve = null; // Clear to prevent multiple resolves
          resolveFn(maskCanvas);
        }
      });

      selfieSegmentationInstance = segmenter;
      resolve(segmenter);
    } catch (err) {
      segmenterPromise = null;
      reject(err);
    }
  });

  return segmenterPromise;
}

/**
 * Segments the foreground (person) from an image, returning a binary canvas mask.
 * White (255) pixels represent the person, Black (0) represents the background.
 */
export async function segmentImage(
  image: HTMLImageElement | HTMLCanvasElement, 
  onProgress?: () => void
): Promise<HTMLCanvasElement> {
  const segmenter = await initSegmenter(onProgress);
  
  return new Promise((resolve, reject) => {
    // Set timeout to prevent hanging if model fails to run
    const timeoutId = setTimeout(() => {
      if (currentResolve === resolve) {
        currentResolve = null;
        reject(new Error('AI Segmentation timed out.'));
      }
    }, 15000);

    currentResolve = (maskCanvas) => {
      clearTimeout(timeoutId);
      resolve(maskCanvas);
    };

    try {
      segmenter.send({ image });
    } catch (error) {
      clearTimeout(timeoutId);
      currentResolve = null;
      reject(error);
    }
  });
}
